import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Shield, Lock, Eye, EyeOff, Trash2, Monitor, ShieldCheck } from '@tamagui/lucide-icons'
import { useState } from 'react'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 28, maxWidth: 640 })

const PrivacyBanner = styled(View, {
  backgroundColor: '$accentLighter',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$accent',
  padding: 20,
  flexDirection: 'row',
  alignItems: 'center',
  gap: 16,
})

const BannerIcon = styled(View, {
  width: 48, height: 48, borderRadius: '$borderRadius3xl', backgroundColor: '$accent',
  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
})

const Section = styled(View, { gap: 12 })

const SectionHeader = styled(View, {
  flexDirection: 'row', alignItems: 'center', gap: 8,
})

const SectionTitle = styled(Text, {
  fontFamily: '$fontBody', fontWeight: '600', fontSize: 16, color: '$surfaceForeground',
})

const SectionDesc = styled(Text, {
  fontFamily: '$fontBody', fontSize: 13, color: '$sidebarMuted', lineHeight: 20,
})

const SettingRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
  paddingVertical: 8, paddingHorizontal: 12, borderRadius: '$borderRadiusLg',
  borderWidth: 1, borderColor: '$borderDefault',
  gap: 12,
})

const SettingInfo = styled(View, { flex: 1, gap: 2 })

const SettingLabel = styled(Text, {
  fontFamily: '$fontBody', fontSize: 14, fontWeight: '500', color: '$surfaceForeground',
})

const SettingDesc = styled(Text, {
  fontFamily: '$fontBody', fontSize: 12, color: '$sidebarMuted',
})

const Toggle = styled(View, {
  width: 40, height: 22, borderRadius: '$borderRadiusXl', cursor: 'pointer', justifyContent: 'center',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
  variants: {
    active: {
      true: { backgroundColor: '$accent' },
      false: { backgroundColor: '$sidebarActive' },
    },
  } as const,
})

const ToggleKnob = styled(View, {
  width: 18, height: 18, borderRadius: '$borderRadiusLg', backgroundColor: '$surfaceForeground',
  // @ts-expect-error -- web transition
  style: { transition: 'margin-left 150ms ease' },
})

const RadioGroup = styled(View, { gap: 4, paddingLeft: 4 })

const RadioItem = styled(View, {
  flexDirection: 'row', alignItems: 'center', height: 36, gap: 10,
  paddingHorizontal: 12, borderRadius: '$borderRadiusMd', cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const RadioDot = styled(View, {
  width: 16, height: 16, borderRadius: '$borderRadiusLg', borderWidth: 2, borderColor: '$borderDefault',
  alignItems: 'center', justifyContent: 'center',
  variants: { active: { true: { borderColor: '$accent' } } } as const,
})

const RadioFill = styled(View, { width: 8, height: 8, borderRadius: '$borderRadiusSm', backgroundColor: '$accent' })

const SessionRow = styled(View, {
  flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12,
  borderRadius: '$borderRadiusLg', borderWidth: 1, borderColor: '$borderDefault', gap: 12,
})

const SessionBadge = styled(View, {
  backgroundColor: '$accent', borderRadius: '$borderRadiusSm', paddingHorizontal: 6, paddingVertical: 2,
})

const NoIdBanner = styled(View, {
  backgroundColor: '$surface', borderRadius: '$borderRadiusXl', borderWidth: 1, borderColor: '$accent',
  padding: 20, gap: 8,
})

export function PrivacySettings() {
  const theme = useTheme()
  const [e2eEnabled, setE2eEnabled] = useState(true)
  const [e2eVoice, setE2eVoice] = useState(true)
  const [readReceipts, setReadReceipts] = useState(false)
  const [dmPolicy, setDmPolicy] = useState<'everyone' | 'friends' | 'nobody'>('friends')
  const [activityStatus, setActivityStatus] = useState(true)
  const [dataCollection, setDataCollection] = useState(false)

  return (
    <Container>
      <Header><Title>Privacy & Security</Title></Header>
      <ScrollView flex={1}>
        <Body>
          {/* Hero: No ID Banner */}
          <NoIdBanner
            // @ts-expect-error -- web box-shadow
            style={{ boxShadow: theme.shadowGlow?.val || 'none' }}
          >
            <View flexDirection="row" alignItems="center" gap={10}>
              <ShieldCheck size={24} color="$accent" />
              <Text fontFamily="$fontHeading" fontWeight="700" fontSize={18} color="$accent">
                No ID Verification Required
              </Text>
            </View>
            <Text fontFamily="$fontBody" fontSize={14} color="$surfaceForeground" lineHeight={22}>
              Arcus will never ask for your government ID, phone number, or face scan.
              Your identity is yours. We authenticate through cryptographic keys, not personal documents.
            </Text>
            <Text fontFamily="$fontMono" fontSize={12} color="$sidebarMuted">
              Unlike Discord, we believe privacy is a right — not a feature to remove.
            </Text>
          </NoIdBanner>

          {/* E2E Encryption */}
          <Section>
            <SectionHeader>
              <Lock size={18} color="$accent" />
              <SectionTitle>End-to-End Encryption</SectionTitle>
            </SectionHeader>
            <SectionDesc>
              Messages and voice calls are encrypted so that only participants can read or hear them.
              Not even Arcus servers can access your content.
            </SectionDesc>

            <SettingRow>
              <Lock size={16} color="$accent" />
              <SettingInfo>
                <SettingLabel>E2E Encrypted Messages</SettingLabel>
                <SettingDesc>All DMs and opted-in channels use end-to-end encryption</SettingDesc>
              </SettingInfo>
              <Toggle active={e2eEnabled} onPress={() => setE2eEnabled(!e2eEnabled)}>
                <ToggleKnob marginLeft={e2eEnabled ? 20 : 2} />
              </Toggle>
            </SettingRow>

            <SettingRow>
              <Shield size={16} color="$accent" />
              <SettingInfo>
                <SettingLabel>E2E Encrypted Voice</SettingLabel>
                <SettingDesc>Voice and video calls are end-to-end encrypted by default</SettingDesc>
              </SettingInfo>
              <Toggle active={e2eVoice} onPress={() => setE2eVoice(!e2eVoice)}>
                <ToggleKnob marginLeft={e2eVoice ? 20 : 2} />
              </Toggle>
            </SettingRow>

            <PrivacyBanner>
              <BannerIcon>
                <Shield size={24} color="$surface" />
              </BannerIcon>
              <View flex={1} gap={4}>
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surfaceForeground">
                  Zero-Knowledge Architecture
                </Text>
                <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted" lineHeight={18}>
                  Encryption keys are generated on your device and never leave it.
                  Message content is encrypted before it reaches our servers.
                </Text>
              </View>
            </PrivacyBanner>
          </Section>

          {/* DM Privacy */}
          <Section>
            <SectionHeader>
              <Eye size={18} color="$surfaceForeground" />
              <SectionTitle>Direct Message Privacy</SectionTitle>
            </SectionHeader>

            <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
              Control who can send you direct messages.
            </Text>

            <RadioGroup>
              {([
                { value: 'everyone' as const, label: 'Everyone', desc: 'Anyone on Arcus can DM you' },
                { value: 'friends' as const, label: 'Friends Only', desc: 'Only people you\'ve added as friends' },
                { value: 'nobody' as const, label: 'Nobody', desc: 'Block all incoming DMs' },
              ]).map((opt) => (
                <RadioItem key={opt.value} onPress={() => setDmPolicy(opt.value)}>
                  <RadioDot active={dmPolicy === opt.value}>
                    {dmPolicy === opt.value && <RadioFill />}
                  </RadioDot>
                  <View flex={1}>
                    <Text fontFamily="$fontBody" fontSize={14} color="$surfaceForeground">{opt.label}</Text>
                    <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">{opt.desc}</Text>
                  </View>
                </RadioItem>
              ))}
            </RadioGroup>
          </Section>

          {/* Activity & Visibility */}
          <Section>
            <SectionHeader>
              <EyeOff size={18} color="$surfaceForeground" />
              <SectionTitle>Activity & Visibility</SectionTitle>
            </SectionHeader>

            <SettingRow>
              <SettingInfo>
                <SettingLabel>Show Activity Status</SettingLabel>
                <SettingDesc>Let others see when you're online, idle, or in a voice channel</SettingDesc>
              </SettingInfo>
              <Toggle active={activityStatus} onPress={() => setActivityStatus(!activityStatus)}>
                <ToggleKnob marginLeft={activityStatus ? 20 : 2} />
              </Toggle>
            </SettingRow>

            <SettingRow>
              <SettingInfo>
                <SettingLabel>Read Receipts</SettingLabel>
                <SettingDesc>Show others when you've read their messages</SettingDesc>
              </SettingInfo>
              <Toggle active={readReceipts} onPress={() => setReadReceipts(!readReceipts)}>
                <ToggleKnob marginLeft={readReceipts ? 20 : 2} />
              </Toggle>
            </SettingRow>
          </Section>

          {/* Data & Telemetry */}
          <Section>
            <SectionHeader>
              <Trash2 size={18} color="$surfaceForeground" />
              <SectionTitle>Data & Telemetry</SectionTitle>
            </SectionHeader>

            <SettingRow>
              <SettingInfo>
                <SettingLabel>Usage Analytics</SettingLabel>
                <SettingDesc>Help improve Arcus by sharing anonymous usage data</SettingDesc>
              </SettingInfo>
              <Toggle active={dataCollection} onPress={() => setDataCollection(!dataCollection)}>
                <ToggleKnob marginLeft={dataCollection ? 20 : 2} />
              </Toggle>
            </SettingRow>

            <Text fontFamily="$fontBody" fontSize={12} color="$color8" lineHeight={18}>
              Arcus collects minimal data by design. We store only what's needed to deliver your messages.
              No tracking pixels. No ad profiles. No selling your data. Period.
            </Text>
          </Section>

          {/* Active Sessions */}
          <Section>
            <SectionHeader>
              <Monitor size={18} color="$surfaceForeground" />
              <SectionTitle>Active Sessions</SectionTitle>
            </SectionHeader>

            <SessionRow>
              <Monitor size={18} color="$accent" />
              <View flex={1} gap={2}>
                <View flexDirection="row" alignItems="center" gap={6}>
                  <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">
                    macOS — Arcus Desktop
                  </Text>
                  <SessionBadge>
                    <Text fontFamily="$fontBody" fontSize={10} fontWeight="600" color="$surface">Current</Text>
                  </SessionBadge>
                </View>
                <Text fontFamily="$fontMono" fontSize={11} color="$sidebarMuted">
                  Last active: Just now • 192.168.1.x
                </Text>
              </View>
            </SessionRow>

            <SessionRow>
              <Monitor size={18} color="$sidebarMuted" />
              <View flex={1} gap={2}>
                <Text fontFamily="$fontBody" fontSize={14} fontWeight="500" color="$surfaceForeground">
                  Windows — Arcus Desktop
                </Text>
                <Text fontFamily="$fontMono" fontSize={11} color="$sidebarMuted">
                  Last active: 2 days ago • 10.0.0.x
                </Text>
              </View>
              <View
                paddingHorizontal={10} paddingVertical={4} borderRadius="$borderRadiusSm"
                cursor="pointer" borderWidth={1} borderColor="$error"
                hoverStyle={{ backgroundColor: 'rgba(207, 62, 79, 0.1)' }}
              >
                <Text fontFamily="$fontBody" fontSize={11} color="$error">Revoke</Text>
              </View>
            </SessionRow>
          </Section>
        </Body>
      </ScrollView>
    </Container>
  )
}
